import React from 'react';
import Navigation from '../../components/Navigation';
import Footer from '../../components/Footer';
import { CheckCircle, Award, Shield, Leaf } from 'lucide-react';

const Quality = () => {
  const qualityFeatures = [
    {
      icon: CheckCircle,
      title: "CE Certification",
      description: "All our doors meet European Conformity standards for safety, health, and environmental protection.",
      details: ["EN 14351-1 compliance", "Performance testing", "Quality documentation", "Regular audits"]
    },
    {
      icon: Award,
      title: "Premium Materials",
      description: "We use only the finest FSC-certified wood and high-grade components in our manufacturing process.",
      details: ["FSC certified timber", "Premium hardware", "Quality insulation", "Durable finishes"]
    },
    {
      icon: Shield,
      title: "Rigorous Testing",
      description: "Every door undergoes comprehensive testing for durability, weather resistance, and performance.",
      details: ["Weather resistance", "Thermal performance", "Security testing", "Durability assessment"]
    },
    {
      icon: Leaf,
      title: "Sustainable Production",
      description: "Committed to environmentally responsible manufacturing with sustainable materials and processes.",
      details: ["Eco-friendly materials", "Waste reduction", "Energy efficiency", "Carbon footprint reduction"]
    }
  ];

  const certifications = [
    { name: "CE Marking", description: "European Conformity certification" },
    { name: "FSC Certified", description: "Forest Stewardship Council approved materials" },
    { name: "ISO 9001", description: "Quality management system standard" },
    { name: "Energy Efficient", description: "Low U-value thermal performance" }
  ];

  const pageStyle = {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #000000 0%, #1a1a1a 50%, #2d2d2d 100%)',
    paddingTop: '4rem'
  };

  const containerStyle = {
    maxWidth: '72rem',
    margin: '0 auto',
    padding: '2rem 1.5rem'
  };

  const headerStyle = {
    textAlign: 'center' as const,
    marginBottom: '4rem'
  };

  const titleStyle = {
    fontSize: '3rem',
    fontWeight: 'bold',
    color: 'white',
    marginBottom: '1rem'
  };

  const highlightStyle = {
    color: '#dfa926'
  };

  const descriptionStyle = {
    fontSize: '1.25rem',
    color: '#d1d5db',
    maxWidth: '48rem',
    margin: '0 auto',
    lineHeight: '1.6'
  };

  const gridStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
    gap: '2rem',
    marginBottom: '4rem'
  };

  const cardStyle = {
    background: 'linear-gradient(135deg, rgba(26, 26, 26, 0.5) 0%, rgba(17, 17, 17, 0.5) 100%)',
    padding: '2rem',
    borderRadius: '0.75rem',
    backdropFilter: 'blur(8px)',
    border: '1px solid rgba(58, 58, 58, 0.5)',
    transition: 'all 0.3s ease'
  };

  const iconStyle = {
    width: '3rem',
    height: '3rem',
    color: '#dfa926',
    marginBottom: '1.5rem'
  };

  const cardTitleStyle = {
    fontSize: '1.5rem',
    fontWeight: '600',
    color: 'white',
    marginBottom: '1rem'
  };

  const cardDescStyle = {
    color: '#9ca3af',
    marginBottom: '1.5rem',
    lineHeight: '1.6'
  };

  const certGridStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '1rem'
  };

  const certCardStyle = {
    background: 'rgba(223, 169, 38, 0.1)',
    border: '1px solid rgba(223, 169, 38, 0.3)',
    borderRadius: '0.5rem',
    padding: '1.5rem',
    textAlign: 'center' as const
  };

  const certTitleStyle = {
    color: '#dfa926',
    fontWeight: '600',
    marginBottom: '0.5rem'
  };

  const certDescStyle = {
    color: '#d1d5db',
    fontSize: '0.875rem'
  };

  return (
    <div style={pageStyle}>
      <Navigation />
      
      <div style={containerStyle}>
        <div style={headerStyle}>
          <h1 style={titleStyle}>
            Quality <span style={highlightStyle}>Assurance</span>
          </h1>
          <p style={descriptionStyle}>
            Our commitment to excellence ensures every door meets the highest standards 
            of quality, durability, and performance.
          </p>
        </div>

        <div style={gridStyle}>
          {qualityFeatures.map((feature, index) => {
            const IconComponent = feature.icon;
            return (
              <div 
                key={index}
                style={cardStyle}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = 'rgba(223, 169, 38, 0.5)';
                  e.currentTarget.style.transform = 'translateY(-4px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = 'rgba(58, 58, 58, 0.5)';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                <IconComponent style={iconStyle} />
                <h3 style={cardTitleStyle}>{feature.title}</h3>
                <p style={cardDescStyle}>{feature.description}</p>
                
                <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                  {feature.details.map((detail, detailIndex) => (
                    <li key={detailIndex} style={{
                      display: 'flex',
                      alignItems: 'center',
                      marginBottom: '0.5rem',
                      color: '#d1d5db',
                      fontSize: '0.875rem'
                    }}>
                      <span style={{
                        width: '0.5rem',
                        height: '0.5rem',
                        backgroundColor: '#dfa926',
                        borderRadius: '50%',
                        marginRight: '0.75rem'
                      }}></span>
                      <span>{detail}</span>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>

        <div style={{ marginBottom: '4rem' }}>
          <h2 style={{
            fontSize: '2rem',
            fontWeight: 'bold',
            color: 'white',
            textAlign: 'center',
            marginBottom: '2rem'
          }}>
            Certifications & <span style={highlightStyle}>Standards</span>
          </h2>
          
          <div style={certGridStyle}>
            {certifications.map((cert, index) => (
              <div key={index} style={certCardStyle}>
                <h4 style={certTitleStyle}>{cert.name}</h4>
                <p style={certDescStyle}>{cert.description}</p>
              </div>
            ))}
          </div>
        </div>

        <div style={{ textAlign: 'center' }}>
          <div style={{
            background: 'linear-gradient(135deg, rgba(223, 169, 38, 0.1) 0%, rgba(223, 169, 38, 0.05) 100%)',
            padding: '3rem',
            borderRadius: '0.75rem',
            border: '1px solid rgba(223, 169, 38, 0.2)'
          }}>
            <h3 style={{ color: 'white', fontSize: '2rem', marginBottom: '1rem' }}>
              Quality Guarantee
            </h3>
            <p style={{ color: '#d1d5db', marginBottom: '2rem', fontSize: '1.125rem' }}>
              We stand behind our products with comprehensive warranties and ongoing support. 
              Every door comes with our quality guarantee and professional service commitment.
            </p>
            <a 
              href="/contact" 
              style={{
                backgroundColor: '#dfa926',
                color: 'black',
                padding: '0.75rem 2rem',
                borderRadius: '0.5rem',
                textDecoration: 'none',
                fontWeight: '600',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => (e.target as HTMLElement).style.backgroundColor = '#c8941f'}
              onMouseLeave={(e) => (e.target as HTMLElement).style.backgroundColor = '#dfa926'}
            >
              Learn More
            </a>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Quality;
